/**
 * Word Settings Component
 * Difficulty level selector and timer for word practice
 */

import { useState } from 'react'
import PillSelect from './PillSelect'
import { useSettingsStore } from '@stores/settingsStore'
import { useSpeechStore } from '@stores/speechStore'
import { WORD_PRACTICE } from '@config/constants'
import { WORD_PRACTICE_BANK } from '@lib/problemGenerator'

interface WordSettingsProps {
  isOpen: boolean
  onToggle: (open: boolean) => void
}

export default function WordSettings({ isOpen, onToggle }: WordSettingsProps) {
  const settings = useSettingsStore(state => state.settings)
  const updateWordPractice = useSettingsStore(state => state.updateWordPractice)
  const speech = useSpeechStore(state => state.speech)
  const toggleListenEnabled = useSpeechStore(state => state.toggleListenEnabled)
  const [expandedDifficulty, setExpandedDifficulty] = useState(false)

  const difficultyLevels = Array.from({ length: WORD_PRACTICE.MAX_DIFFICULTY }, (_, i) => i + 1)
  const difficultyLabels: Record<number, string> = {
    1: 'Level 1 - Starter words',
    2: 'Level 2 - Short vowels',
    3: 'Level 3 - Blends',
    4: 'Level 4 - Complex patterns',
    5: 'Level 5 - Challenge words',
    6: 'Level 6 - Readers',
    7: 'Level 7 - Multi-syllable',
    8: 'Level 8 - Academic',
    9: 'Level 9 - Advanced',
    10: 'Level 10 - Mastery',
  }

  return (
    <details
      className="config__section"
      open={isOpen}
      onToggle={e => onToggle(e.currentTarget.open)}
    >
      <summary className="config__section-summary">
        <span className="config__section-title">Word Practice</span>
      </summary>
      <fieldset className="config__group config__group--framed">
        <legend>Word practice options</legend>
        <p className="config__hint config__hint--spaced">
          Pick the level for word practice.
        </p>
        <div className="config__operations-grid">
          <div className="config__operation-tile">
            {!expandedDifficulty ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedDifficulty(true)}
              >
                <span className="config__operation-collapsed-label">Difficulty</span>
                <span className="config__operation-collapsed-value">
                  Level {settings.wordPractice.difficulty}
                </span>
              </button>
            ) : null}

            {expandedDifficulty ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Difficulty</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Level"
                    ariaLabel="Word practice difficulty"
                    value={settings.wordPractice.difficulty}
                    options={difficultyLevels.map(level => ({
                      value: level,
                      label: difficultyLabels[level],
                      shortLabel: `Level ${level}`,
                    }))}
                    onChange={value => updateWordPractice({ difficulty: value as number })}
                    icon="📚"
                  />
                  <p className="config__hint config__hint--inline">
                    1-5: reading with on-device listening. 6-10: spelling with spoken prompts.
                  </p>
                  <details className="config__word-preview">
                    <summary className="config__word-preview-summary">
                      <span className="config__operation-subtitle">Preview</span>
                      <span className="config__operation-hint">First few words for this level.</span>
                    </summary>
                    <ul className="config__word-preview-list">
                      {(WORD_PRACTICE_BANK[settings.wordPractice.difficulty] || [])
                        .slice(0, WORD_PRACTICE.PREVIEW_COUNT)
                        .map(word => (
                          <li key={word}>{word}</li>
                        ))}
                    </ul>
                  </details>
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedDifficulty(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={`config__operation-collapsed ${!speech.listenEnabled ? 'config__operation-collapsed--disabled' : ''}`}
          >
            <label className="config__operation-toggle">
              <input
                type="checkbox"
                checked={speech.listenEnabled}
                onChange={toggleListenEnabled}
                className="config__operation-toggle-input"
              />
              <span className="config__operation-toggle-slider"></span>
            </label>
            <div className="config__operation-collapsed-content config__operation-content--static">
              <span className="config__operation-collapsed-label">Listening</span>
              <span className="config__operation-collapsed-value">
                {speech.listenEnabled ? 'On' : 'Off'}
              </span>
            </div>
          </div>
        </div>
      </fieldset>
    </details>
  )
}
